import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MaterialReactTable from 'material-react-table';
import { useAxios } from '../../contexts/axios';
import { BACKEND_URL } from '../../configs';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ExportToCsv } from 'export-to-csv';
import { useAuth } from '../../contexts/auth';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import {
    getTeamSelectTextFieldProps,
    normalizeTeamFieldForRow,
    normalizedTeamEditValue,
    teamCellDisplayLabel,
    teamSelectMenuItems,
} from './taggingTeamSelect';

function coerceDateToIso(d) {
    if (d == null || d === '') return '';
    if (typeof d === 'string') return d;
    if (typeof d === 'object' && d.$date != null) {
        const x = d.$date;
        if (typeof x === 'string') return x;
        if (typeof x === 'number') return new Date(x).toISOString();
        if (x instanceof Date) return x.toISOString();
        return String(x);
    }
    if (d instanceof Date) return d.toISOString();
    return String(d);
}

function formatCreatedAtForCell(d) {
    const iso = coerceDateToIso(d);
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function parseRedisMetadataRow(raw) {
    if (!raw || typeof raw !== 'object') return raw;
    const __apiSource = { ...raw };
    return {
        ...__apiSource,
        subscription_name: __apiSource.subscription_name ?? '',
        database_name: __apiSource.database_name ?? '',
        team: normalizeTeamFieldForRow(__apiSource.team ?? ''),
        vertical: __apiSource.vertical ?? '',
        application: __apiSource.application ?? '',
        owner: __apiSource.owner ?? '',
        created_at_iso: coerceDateToIso(__apiSource.created_at),
        __apiSource,
    };
}

function normalizeListResponse(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
}

function isOptionalTagField(columnId) {
    return ['team', 'application', 'owner'].includes(columnId);
}

/** POST body from create form. */
function toPostPayload(values) {
    const subscriptionId = Number(values.subscription_id);
    const databaseId = Number(values.database_id);
    return {
        subscription_id: Number.isFinite(subscriptionId)
            ? subscriptionId
            : values.subscription_id,
        subscription_name: String(values.subscription_name ?? ''),
        database_id: Number.isFinite(databaseId) ? databaseId : values.database_id,
        database_name: String(values.database_name ?? ''),
        team: String(values.team ?? ''),
        vertical: String(values.vertical ?? ''),
        application: String(values.application ?? ''),
        owner: String(values.owner ?? ''),
    };
}

function redisApiRowForRequest(row) {
    if (row?.__apiSource && typeof row.__apiSource === 'object') {
        return { ...row.__apiSource };
    }
    const { created_at_iso, __apiSource, ...rest } = row ?? {};
    return { ...rest };
}

export default function RedisTagging() {
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const axios = useAxios();
    const { isAdmin } = useAuth();

    function call(url, data, method) {
        const requestOptions = {
            url: `${BACKEND_URL}${'/redis/database-metadata'}${url}`,
            method: method,
            data: data,
        };
        axios(requestOptions);
    }

    useEffect(() => {
        axios({ url: `${BACKEND_URL}${'/redis/database-metadata'}` }).then(function (
            res
        ) {
            const list = normalizeListResponse(res.data).map(parseRedisMetadataRow);
            setTableData(list);
        });
    }, []);

    const handleCreateNewRow = (values) => {
        if (String(values.database_name ?? '').trim().length <= 2) {
            alert('Please enter valid database name, too few characters');
            return;
        }
        const sid = Number(values.subscription_id);
        const did = Number(values.database_id);
        if (!Number.isFinite(sid) || !Number.isFinite(did)) {
            alert('Subscription ID and Database ID must be valid numbers');
            return;
        }

        const payload = toPostPayload(values);
        const optimistic = parseRedisMetadataRow({
            ...payload,
            created_at: new Date().toISOString(),
        });
        tableData.push(optimistic);
        setTableData([...tableData]);
        call('', payload, 'POST');
    };

    const handleSaveRowEdits = async ({ exitEditingMode, row, values }) => {
        if (!Object.keys(validationErrors).length) {
            const merged = parseRedisMetadataRow({
                ...redisApiRowForRequest(row.original),
                team: values.team,
                application: values.application,
                owner: values.owner,
            });
            tableData[row.index] = merged;
            setTableData([...tableData]);
            exitEditingMode();

            call(
                '',
                {
                    ...redisApiRowForRequest(row.original),
                    team: values.team,
                    application: values.application,
                    owner: values.owner,
                },
                'PUT'
            );
        }
    };

    const handleDeleteRow = useCallback(
        (row) => {
            if (
                !window.confirm(
                    `Are you sure you want to delete ${row.getValue('database_name')}`
                )
            ) {
                return;
            }
            const payload = redisApiRowForRequest(row.original);
            tableData.splice(row.index, 1);
            setTableData([...tableData]);
            call('', payload, 'DELETE');
        },
        [tableData]
    );

    const getCommonEditTextFieldProps = useCallback(
        (cell) => {
            return {
                error: !!validationErrors[cell.id],
                helperText: validationErrors[cell.id],
                onBlur: (event) => {
                    const value = event.target.value;
                    const isValid = isOptionalTagField(cell.column.id)
                        ? true
                        : validateRequired(value);
                    if (!isValid) {
                        setValidationErrors({
                            ...validationErrors,
                            [cell.id]: `${cell.column.columnDef.header} is required`,
                        });
                    } else {
                        delete validationErrors[cell.id];
                        setValidationErrors({
                            ...validationErrors,
                        });
                    }
                },
            };
        },
        [validationErrors]
    );

    const columns = useMemo(
        () => [
            {
                accessorKey: 'subscription_id',
                header: 'SUBSCRIPTION ID',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'subscription_name',
                header: 'SUBSCRIPTION NAME',
                size: 180,
                enableEditing: false,
            },
            {
                accessorKey: 'database_id',
                header: 'DATABASE ID',
                size: 140,
                enableEditing: false,
            },
            {
                accessorKey: 'database_name',
                header: 'DATABASE NAME',
                size: 180,
                enableEditing: false,
            },
            {
                accessorKey: 'team',
                header: 'TEAM',
                Cell: ({ cell }) => teamCellDisplayLabel(cell.getValue()),
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getTeamSelectTextFieldProps(),
                    select: true,
                    children: teamSelectMenuItems(),
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'vertical',
                header: 'VERTICAL',
                enableEditing: false,
            },
            {
                accessorKey: 'application',
                header: 'APPLICATION',
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'owner',
                header: 'OWNER',
                muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                    ...getCommonEditTextFieldProps(cell),
                }),
            },
            {
                accessorKey: 'created_at_iso',
                header: 'CREATED AT',
                size: 180,
                enableEditing: false,
                Cell: ({ row }) =>
                    formatCreatedAtForCell(row.original.created_at),
                meta: { skipInCreate: true },
            },
        ],
        [getCommonEditTextFieldProps]
    );

    const createFormColumns = useMemo(
        () =>
            columns.filter(
                (c) => c.accessorKey && !c.meta?.skipInCreate
            ),
        [columns]
    );

    const csvOptions = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        useBom: true,
        filename: 'Redis Tagging Data ' + new Date().toDateString(),
        useKeysAsHeaders: false,
        headers: [
            'SUBSCRIPTION ID',
            'SUBSCRIPTION NAME',
            'DATABASE ID',
            'DATABASE NAME',
            'TEAM',
            'VERTICAL',
            'APPLICATION',
            'OWNER',
            'CREATED AT',
        ],
    };

    const csvExporter = new ExportToCsv(csvOptions);

    const handleExportData = () => {
        const data = tableData.map((row) => ({
            subscription_id: row.subscription_id,
            subscription_name: row.subscription_name,
            database_id: row.database_id,
            database_name: row.database_name,
            team: row.team,
            vertical: row.vertical,
            application: row.application,
            owner: row.owner,
            created_at: row.created_at_iso ?? coerceDateToIso(row.created_at),
        }));
        csvExporter.generateCsv(data);
    };

    return (
        <>
            <MaterialReactTable
                displayColumnDefOptions={{
                    'mrt-row-actions': {
                        muiTableHeadCellProps: {
                            align: 'center',
                        },
                        size: 120,
                    },
                }}
                columns={columns}
                data={tableData}
                getRowId={(row) =>
                    `${row.subscription_id}-${row.database_id}-${row.database_name}`
                }
                editingMode="modal"
                enableColumnOrdering
                enableStickyFooter
                enableStickyHeader
                enableEditing
                enableDensityToggle={false}
                initialState={{
                    columnPinning: {
                        left: ['mrt-row-actions', 'database_name'],
                    },
                    density: 'compact',
                }}
                enableRowActions
                renderRowActions={({ row, table }) => (
                    <Box sx={{ display: 'flex', gap: '1rem' }}>
                        <Tooltip arrow placement="left" title="Edit">
                            <IconButton
                                disabled={!isAdmin()}
                                onClick={() => table.setEditingRow(row)}
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <Tooltip arrow placement="right" title="Delete">
                            <IconButton
                                disabled={!isAdmin()}
                                color="error"
                                onClick={() => handleDeleteRow(row)}
                            >
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
                positionToolbarAlertBanner="bottom"
                renderTopToolbarCustomActions={() => (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '1rem',
                            p: '0.5rem',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            color="success"
                            onClick={() => setCreateModalOpen(true)}
                            variant="contained"
                            disabled={!isAdmin()}
                        >
                            Add New Instance
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleExportData}
                            startIcon={<FileDownloadIcon />}
                            variant="contained"
                        >
                            Export All Data
                        </Button>
                    </Box>
                )}
                onEditingRowSave={handleSaveRowEdits}
            />
            <AddNewUser
                columns={createFormColumns}
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSubmit={handleCreateNewRow}
            />
        </>
    );
}

export const AddNewUser = ({ open, columns, onClose, onSubmit }) => {
    const [values, setValues] = useState(() =>
        columns.reduce((acc, column) => {
            acc[column.accessorKey ?? ''] = '';
            return acc;
        }, {})
    );
    const handleSubmit = () => {
        onSubmit(values);
        onClose();
    };

    return (
        <Dialog open={open}>
            <DialogTitle textAlign="center">Add Redis Instance Details</DialogTitle>
            <DialogContent>
                <form onSubmit={(e) => e.preventDefault()}>
                    <Stack
                        sx={{
                            width: '100%',
                            minWidth: { xs: '300px', sm: '360px', md: '400px' },
                            gap: '1.5rem',
                        }}
                    >
                        {columns.map(function (object) {
                            if (object.accessorKey === 'team') {
                                return (
                                    <TextField
                                        {...getTeamSelectTextFieldProps()}
                                        key="team"
                                        select
                                        fullWidth
                                        label={object.header}
                                        name="team"
                                        value={normalizedTeamEditValue(
                                            values.team
                                        )}
                                        onChange={(e) =>
                                            setValues({
                                                ...values,
                                                team: e.target.value,
                                            })
                                        }
                                    >
                                        {teamSelectMenuItems()}
                                    </TextField>
                                );
                            }
                            return (
                                <TextField
                                    key={object.accessorKey}
                                    label={object.header}
                                    name={object.accessorKey}
                                    type={
                                        object.accessorKey === 'subscription_id' ||
                                        object.accessorKey === 'database_id'
                                            ? 'number'
                                            : 'text'
                                    }
                                    onChange={(e) =>
                                        setValues({
                                            ...values,
                                            [e.target.name]: e.target.value,
                                        })
                                    }
                                />
                            );
                        })}
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions sx={{ p: '1.25rem' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="secondary"
                    onClick={handleSubmit}
                    variant="contained"
                >
                    Add New Instance
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const validateRequired = (value) => !!String(value ?? '').trim().length;
const validateEmail = (email) =>
    !!email.length &&
    email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
